import { isEqual, omit } from 'es-toolkit/compat';
import { decodeToken } from 'react-jwt';
import { TablePageSize, User } from 'types';
import { FirmTheme } from 'types/api/firms';
import { getInitialTheme } from 'utils/themeUtils';
import create from 'zustand';
import { devtools } from 'zustand/middleware';

export interface LayoutStore {
  loading: boolean;
  setLoading: (value: boolean) => void;
  tablesPageSizes: TablePageSize[];
  setTablePageSize: (tablesPageSizes: TablePageSize[]) => void;
  theme: FirmTheme;
  confirmedMobileUnsupported: boolean;
  setConfirmedMobileUnsupported: (value: boolean) => void;
  noScansInCompany: boolean;
  setNoScansInCompany: (value: boolean) => void;
  setTheme: (theme: FirmTheme, persist?: boolean) => void;
  type: 'user' | 'admin' | 'firmadmin' | 'superadmin' | null;
  setType: (type: 'user' | 'admin' | 'firmadmin' | 'superadmin' | null) => void;
  reset: () => void;
  applyTheme: (token: string) => void;
}

export const useLayoutStore = create<LayoutStore>()(
  devtools((set, getState, store) => ({
    loading: true,
    setLoading: value => set({ loading: value }),
    tablesPageSizes: JSON.parse(window.localStorage.getItem('sc_tables_page_sizes') || '[]'),
    setTablePageSize: tablesPageSizes => set({ tablesPageSizes }),
    theme: getInitialTheme(),
    confirmedMobileUnsupported: false,
    setConfirmedMobileUnsupported: (value: boolean) => set({ confirmedMobileUnsupported: value }),
    noScansInCompany: false,
    setNoScansInCompany: (value: boolean) => set({ noScansInCompany: value }),
    setTheme: (newTheme, persist = false) => {
      if (persist) {
        localStorage.setItem('sc_theme', JSON.stringify(newTheme));
      }

      set({ theme: newTheme });
    },
    type: null,
    setType: type => {
      if (getState().type === type) {
        return;
      }

      set({ type });
    },
    reset: () => {
      if (isEqual(omit(getState(), ['loading', 'theme']), omit(store.getInitialState(), ['loading', 'theme']))) return;

      // localStorage.removeItem('sc_theme');
      set({ ...store.getInitialState(), theme: getState().theme });
    },
    applyTheme: (token: string) => {
      localStorage.removeItem('sc_theme_partner');
      const decodedToken: User = decodeToken(token)!;

      getState().setTheme(
        {
          logo_primary_light: decodedToken.logo_primary_light,
          logo_primary_dark: decodedToken.logo_primary_dark,
          logo_secondary_light: decodedToken.logo_secondary_light,
          logo_secondary_dark: decodedToken.logo_secondary_dark,
          theme: decodedToken.theme,
          color_scheme_light: decodedToken.color_scheme_light,
          color_scheme_dark: decodedToken.color_scheme_dark
        },
        true
      );
    }
  }))
);
